import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { observer } from 'mobx-react-lite'

// evertel
import { Col, Row, Modal, ModalBody, ModalHeader, RoomSearchSelect, Text, Button, Icon, useUI } from '@evertel/web/ui'
import { SessionService } from '@evertel/session/SessionService'
import { useService } from '@evertel/di'
import { DepartmentController } from '@evertel/department'
import { APIDataRoom } from '@evertel/types'
import { MessageForwardController, MessageWallContext } from '@evertel/message'
import { ForwardMessageInput } from './forward/ForwardMessageInput'
import { ForwardMessagePreview } from './forward/ForwardMessagePreview'
interface ForwardMessageModalProps {
    visible: boolean
    onClose: () => void
    messageId?: number
}

const ForwardMessageModal: React.FC<ForwardMessageModalProps> = observer(({
    visible,
    onClose,
    messageId
}) => {

    const sessionService = useService(SessionService, [])
    const navigate = useNavigate()
    const { addToast } = useUI()

    const {
        modelData // room data | thread data
    } = useContext(MessageWallContext)

    const departmentId = (modelData as APIDataRoom)?.departmentId || sessionService.selectedDepartmentId

    const forwardMessageController = useService(MessageForwardController, [])
    const departmentController = useService(DepartmentController, [departmentId])

    const [selectedRoom, setSelectedRoom] = useState<APIDataRoom>(null)
    const [message, setMessage] = useState<string>('')
    const [isUrgent, setIsUrgent] = useState<boolean>(false)
    const [loading, setIsLoading] = useState(false)

    useEffect(() => {
        forwardMessageController.init(messageId)
    }, [forwardMessageController, messageId])

    useEffect(() => {
        departmentController.init(departmentId)
    }, [departmentId])

    const onCloseModal = () => {
        setSelectedRoom(null)
        setMessage('')
        setIsUrgent(false)

        onClose()
    }

    const toggleUrgent = () => {
        setIsUrgent(!isUrgent)
    }

    const onChangeMessage = (message: string) => {
        setMessage(message)
    }

    const onSubmit = async () => {
        if (!selectedRoom?.id) {
            addToast({
                title: 'Error',
                color: 'danger',
                message: 'No room selected. Try again by adding a room'
            })

            return
        }

        setIsLoading(true)

        const data = { roomId: selectedRoom?.id, messageText: message, isUrgent: isUrgent }

        try {
            await forwardMessageController.forwardMessage(messageId, data)
        } catch (error: any) {
            addToast({
                title: 'Error',
                color: 'danger',
                message: error.message
            })
            setIsLoading(false)
            return
        }
        navigate(`/room/${selectedRoom?.id}`)
        onCloseModal()
        setIsLoading(false)
    }

    return (
        <Modal
            visible={visible}
            onClose={onCloseModal}
            keyboard={true}
            size="lg"
            style={{ padding: 15 }}>
            <ModalHeader>
                <Col>
                    <Text children={'Forward this message'} tag={'h2'} bold={true} />
                    <Text children={`Select 1 room inside ${departmentController.department?.name || 'AGENCY'}`} tag={'h5'} />
                </Col>
            </ModalHeader>
            <ModalBody>
                <Col>
                    <Row style={{ paddingBottom: 40, zIndex: 10 }}>
                        <RoomSearchSelect
                            className="w-100"
                            searchScope="forward"
                            placeholder="Select room..."
                            departmentId={departmentId}
                            isMulti={false}
                            selected={[selectedRoom]}
                            onSelect={(room) => setSelectedRoom(room as APIDataRoom)}
                        />
                    </Row>
                    <Row>
                        <ForwardMessageInput
                            message={message}
                            isUrgent={isUrgent}
                            toggleUrgent={toggleUrgent}
                            onChangeMessage={onChangeMessage}
                        />
                    </Row>
                    <Row id="forwardMessageInput" />
                    <Row style={{ paddingTop: 40, paddingBottom: 40 }}>
                        <ForwardMessagePreview forwardMessage={forwardMessageController.message} />
                    </Row>
                    <Row align="right">
                        <Button
                            color="primary"
                            loading={loading}
                            onClick={onSubmit}>
                            <Icon name={'share'} /> Forward
                        </Button>
                    </Row>
                </Col>
            </ModalBody>
        </Modal>
    )
})

export {
    ForwardMessageModal
}
